<template>
  <v-container fluid class="portfolio-page">
    <v-row no-gutters class="d-flex justify-space-between align-center mt-10 mb-4">
      <h1 class="page-title mb-0">Portfolio</h1>
      <div>
        <v-btn
          v-if="!reorderMode"
          @click="enterReorderMode"
          color="primary"
          outlined
          class="text-capitalize mr-2"
        >
          <v-icon left small>mdi-swap-vertical</v-icon>
          Reorder
        </v-btn>
        <template v-else>
          <v-btn
            @click="saveOrder"
            color="success"
            :loading="savingOrder"
            class="text-capitalize mr-2"
          >
            <v-icon left small>mdi-content-save</v-icon>
            Save Order
          </v-btn>
          <v-btn
            @click="cancelReorder"
            outlined
            class="text-capitalize mr-2"
            :disabled="savingOrder"
          >
            Cancel
          </v-btn>
        </template>
        <v-btn
          @click="$router.push('/add_portfolio')"
          color="secondary"
          class="text-capitalize button-shadow"
        >
          <v-icon left small>mdi-plus</v-icon>
          Add Item
        </v-btn>
      </div>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-card class="mx-1 mb-1">
          <v-card-title class="pa-6 pb-3">
            <p>Gallery</p>
            <v-spacer></v-spacer>
            <v-chip v-if="reorderMode" color="primary" small outlined class="mr-2">
              <v-icon left x-small>mdi-cursor-move</v-icon>
              Drag to reorder
            </v-chip>
            <span class="caption card-light-grey mt-1">
              {{ filteredItems.length }} item{{ filteredItems.length !== 1 ? 's' : '' }}
            </span>
          </v-card-title>
          <v-card-text class="pa-6 pt-0">
            <!-- Filter Buttons (hidden in reorder mode) -->
            <div v-if="!reorderMode" class="mb-4">
              <v-btn
                small
                class="mx-1 mb-1 text-capitalize"
                :color="currentTag === 'all' ? 'primary' : ''"
                :outlined="currentTag !== 'all'"
                @click="filter('all')"
              >All</v-btn>
              <v-btn
                v-for="category in categories"
                :key="category.id"
                small
                class="mx-1 mb-1 text-capitalize"
                :color="currentTag === category.id ? 'primary' : ''"
                :outlined="currentTag !== category.id"
                @click="filter(category.id)"
              >{{ category.name }}</v-btn>
            </div>

            <!-- Loading -->
            <div v-if="isLoading" class="d-flex justify-center py-10">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </div>

            <!-- Empty State -->
            <div v-else-if="!filteredItems.length" class="text-center py-10">
              <v-icon x-large color="grey lighten-1">mdi-image-off</v-icon>
              <div class="card-light-grey mt-3">No portfolio items found</div>
              <v-btn
                small
                color="primary"
                class="mt-4 text-capitalize"
                @click="$router.push('/add_portfolio')"
              >Add Portfolio Item</v-btn>
            </div>

            <!-- Reorder Mode: Draggable Grid -->
            <draggable
              v-else-if="reorderMode"
              v-model="reorderItems"
              class="portfolio-grid reorder-grid"
              ghost-class="drag-ghost"
              animation="200"
              handle=".drag-handle"
            >
              <div
                v-for="(item, index) in reorderItems"
                :key="item.id"
                class="portfolio-grid-item drag-handle"
              >
                <v-card outlined class="portfolio-card reorder-card">
                  <v-img
                    :src="url + item.image"
                    height="180"
                    class="grey lighten-3"
                  >
                    <template v-slot:placeholder>
                      <v-row class="fill-height ma-0" align="center" justify="center">
                        <v-progress-circular indeterminate color="grey lighten-2"></v-progress-circular>
                      </v-row>
                    </template>
                  </v-img>
                  <div class="reorder-overlay">
                    <v-icon color="white" class="mr-1">mdi-drag</v-icon>
                    <span class="white--text caption font-weight-bold">#{{ index + 1 }}</span>
                  </div>
                </v-card>
              </div>
            </draggable>

            <!-- Normal Mode: Portfolio Grid -->
            <transition-group
              v-else
              name="list-complete"
              class="portfolio-grid"
              tag="div"
            >
              <div
                v-for="item in filteredItems"
                :key="item.id"
                class="portfolio-grid-item"
              >
                <v-hover v-slot:default="{ hover }">
                  <v-card outlined class="portfolio-card">
                    <v-img
                      :src="url + item.image"
                      height="180"
                      class="grey lighten-3"
                    >
                      <template v-slot:placeholder>
                        <v-row class="fill-height ma-0" align="center" justify="center">
                          <v-progress-circular indeterminate color="grey lighten-2"></v-progress-circular>
                        </v-row>
                      </template>
                      <v-fade-transition>
                        <div
                          v-if="hover"
                          class="d-flex align-center justify-center portfolio-overlay"
                        >
                          <v-btn
                            fab
                            small
                            color="error"
                            @click.stop="confirmDelete(item)"
                          >
                            <v-icon>mdi-delete</v-icon>
                          </v-btn>
                        </div>
                      </v-fade-transition>
                    </v-img>
                  </v-card>
                </v-hover>
              </div>
            </transition-group>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Delete Dialog -->
    <v-dialog v-model="deleteDialog" max-width="450">
      <v-card>
        <v-card-title class="text-h6">
          <v-icon left color="error">mdi-alert-circle</v-icon>
          Confirm Delete
        </v-card-title>
        <v-card-text>
          Are you sure you want to delete this portfolio item? This action cannot be undone.
        </v-card-text>
        <v-card-actions class="pa-4 pt-0">
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="deleteDialog = false"
            :disabled="deleteLoading"
          >Cancel</v-btn>
          <v-btn
            color="error"
            :loading="deleteLoading"
            @click="deletePortfolio(toDelete)"
          >Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from "axios";
import draggable from "vuedraggable";

const url = process.env.VUE_APP_STORAGE_URL;

export default {
  name: "Portfolio",
  components: {
    draggable,
  },
  data: () => ({
    categories: [],
    items: [],
    reorderItems: [],
    reorderMode: false,
    savingOrder: false,
    currentTag: "all",
    url: url,
    isLoading: false,
    deleteDialog: false,
    deleteLoading: false,
    toDelete: "",
  }),
  methods: {
    filter(tag) {
      this.currentTag = tag;
    },
    getCategories() {
      axios.get("portfolio-categories").then(({ data }) => {
        this.categories = data;
      });
    },
    getImages() {
      this.isLoading = true;
      axios.get("portfolios")
        .then(({ data }) => {
          this.items = [];
          if (data) {
            data.forEach(this.imagesToArray);
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    imagesToArray(value) {
      this.items.push({
        id: value.id,
        image: value.image,
        sort_order: value.sort_order || 0,
        tags: ["all", value.portfolio_category_id],
      });
    },
    enterReorderMode() {
      this.currentTag = "all";
      this.reorderItems = this.items.map(item => ({ ...item }));
      this.reorderMode = true;
    },
    cancelReorder() {
      this.reorderMode = false;
      this.reorderItems = [];
    },
    saveOrder() {
      this.savingOrder = true;
      const payload = {
        items: this.reorderItems.map((item, index) => ({
          id: item.id,
          sort_order: index,
        })),
      };

      axios.post("portfolios/reorder", payload)
        .then(() => {
          this.items = this.reorderItems.map((item, index) => ({
            ...item,
            sort_order: index,
          }));
          this.reorderMode = false;
          this.reorderItems = [];
          this.$toast.success("Portfolio order saved successfully");
        })
        .catch(() => {
          this.$toast.error("Failed to save order. Please try again.");
        })
        .finally(() => {
          this.savingOrder = false;
        });
    },
    confirmDelete(item) {
      this.toDelete = item.id;
      this.deleteDialog = true;
    },
    deletePortfolio(id) {
      this.deleteLoading = true;
      axios
        .delete("portfolios/" + id)
        .then(() => {
          this.getImages();
          this.deleteDialog = false;
          this.$toast.success("Portfolio item deleted successfully");
        })
        .catch(() => {
          this.$toast.error("Failed to delete portfolio item");
        })
        .finally(() => {
          this.deleteLoading = false;
        });
    },
  },
  computed: {
    filteredItems() {
      if (this.currentTag === "all") return this.items;
      return this.items.filter(item => item.tags.indexOf(this.currentTag) !== -1);
    },
  },
  mounted() {
    this.getCategories();
    this.getImages();
  },
};
</script>

<style src="./Portfolio.scss" scoped lang="scss"></style>
<style lang="css" scoped>
.portfolio-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 16px;
}
.portfolio-grid-item {
  transition: transform 0.3s ease;
}
.portfolio-card {
  overflow: hidden;
  transition: box-shadow 0.2s ease;
}
.portfolio-card:hover {
  box-shadow: 0 4px 16px rgba(0,0,0,0.15) !important;
}
.portfolio-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
}

/* Reorder mode styles */
.reorder-grid .drag-handle {
  cursor: grab;
}
.reorder-grid .drag-handle:active {
  cursor: grabbing;
}
.reorder-card {
  position: relative;
}
.reorder-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.6);
  padding: 6px 10px;
  display: flex;
  align-items: center;
}
.drag-ghost {
  opacity: 0.4;
  border: 2px dashed #536DFE !important;
  border-radius: 4px;
}

.list-complete-enter, .list-complete-leave-to {
  opacity: 0;
  transform: scale(0.9);
}
.list-complete-enter-active, .list-complete-leave-active {
  transition: all 0.3s ease;
}
</style>
